import { useEffect, useRef, useState } from "react";
import type { PackItemSpec, PackListConfig } from "../scenario/types";
import type { PinScreen } from "../three/PackPinProjector";
import { audio } from "../lib/audio";
import { PackItemReveal } from "./PackItemReveal";

export type PackResult = {
  safe: boolean;
  eventId: string;
  outcome: string;
  label: string;
  packedIds: string[];
};

/**
 * Pack-in-3D-room UI: bag sidebar + pins projected onto the live Marble pano.
 * Look-around is the WebGL OrbitControls (inspect mode) — not a 2D image pan.
 */
export function PackRoom({
  config,
  pinScreens,
  onComplete,
}: {
  config: PackListConfig;
  pinScreens: PinScreen[];
  onComplete: (result: PackResult) => void;
}) {
  const items = config.plateHotspots ?? [];
  const [packed, setPacked] = useState<Set<string>>(() => new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  // Tracks which pins have been tapped at least once so the UI can stop
  // showing the "tap to inspect" affordance on that pin once the learner has
  // already looked inside it (2026-07-15: test user thought a single tap
  // already packed the item — the magnifier badge + fading state make the
  // "this is a look, not a decision" distinction visible on the pin itself,
  // not just in copy she may not have read).
  const [inspectedIds, setInspectedIds] = useState<Set<string>>(() => new Set());
  const [hint, setHint] = useState<string | null>(null);
  const spoke = useRef(false);

  useEffect(() => {
    if (spoke.current) return;
    spoke.current = true;
    const line =
      config.body ??
      "Look around Max's apartment. Tap what he should pack — then confirm the bag.";
    void audio.speak(line, {
      voice: config.voiceId ?? "onyx",
      lang: config.language ?? "en-US",
    });
    return () => audio.cancelSpeech();
  }, [config]);

  const byId = new Map(pinScreens.map((p) => [p.id, p]));

  // Tapping an object now ALWAYS opens the rich "what's really there" detail
  // card first (sticky note's actual handwritten password, the USB's real
  // file browser, etc.) instead of silently toggling the bag. Packing/leaving
  // happens from the button inside that card.
  const inspect = (item: PackItemSpec) => {
    setOpenId(item.id === openId ? null : item.id);
    setInspectedIds((prev) => (prev.has(item.id) ? prev : new Set(prev).add(item.id)));
    setHint(null);
  };

  const doToggle = (item: PackItemSpec) => {
    setPacked((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const toggle = doToggle;

  const packedItems = items.filter((i) => packed.has(i.id) && i.role !== "info");
  const required = items.filter((i) => i.role === "safe" && i.required !== false);
  const traps = items.filter((i) => i.role === "trap");

  const submit = () => {
    const missing = required.filter((i) => !packed.has(i.id));
    const hitTraps = traps.filter((i) => packed.has(i.id));
    const safe = missing.length === 0 && hitTraps.length === 0;

    if (!safe && packed.size === 0) {
      setHint("Look around and tap objects to pack them into Max's bag.");
      return;
    }

    let eventId = "packed_safe";
    let label = "Packed approved gear only";
    if (!safe) {
      if (hitTraps.some((t) => t.id.includes("usb"))) {
        eventId = "packed_usb";
        label = hitTraps.map((t) => t.label).join(", ");
      } else if (hitTraps.length) {
        eventId = "packed_personal";
        label = hitTraps.map((t) => t.label).join(", ");
      } else if (missing.length) {
        eventId = "packed_personal";
        label = `Left behind: ${missing.map((m) => m.label).join(", ")}`;
      } else {
        eventId = "packed_personal";
        label = "Incomplete pack";
      }
    }

    const line = safe ? config.lenaSafe : config.lenaRisky;
    if (line) void audio.speak(line, { voice: "nova", lang: config.language ?? "en-US" });

    onComplete({
      safe,
      eventId,
      outcome: safe ? "safe" : "risky",
      label,
      packedIds: [...packed],
    });
  };

  const open = items.find((i) => i.id === openId) ?? null;

  return (
    <div className="pack-room pack-room--pano" aria-label="Pack Max's bag">
      <div className="pack-room-pins-layer" aria-hidden={false}>
        {items.map((h) => {
          const scr = byId.get(h.id);
          if (!scr?.visible) return null;
          const on = packed.has(h.id);
          const inspected = inspectedIds.has(h.id);
          return (
            <button
              key={h.id}
              type="button"
              className={`hotspot-pin pack-pin ${on ? "on" : ""} ${inspected ? "inspected" : ""} ${h.role === "info" ? "info" : ""} ${h.spriteUrl ? "has-sprite" : ""}`}
              style={{ left: `${scr.xPct}%`, top: `${scr.yPct}%` }}
              onClick={() => inspect(h)}
              title={h.label}
            >
              {h.spriteUrl && (
                <img
                  className="pack-sprite"
                  src={h.spriteUrl}
                  alt=""
                  aria-hidden
                  style={{
                    width: `${(h.spriteWidthPct ?? 9)}vw`,
                    transform:
                      h.spriteAnchor === "center"
                        ? "translate(-50%, -50%)"
                        : "translate(-50%, -100%)",
                  }}
                />
              )}
              <span className="hotspot-dot" aria-hidden />
              {/* Magnifier badge = "tapping this is a LOOK, not a decision".
                  Fades away once the learner has actually opened the reveal
                  card for this object at least once. */}
              {!inspected && (
                <span className="hotspot-inspect-badge" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="10.5" cy="10.5" r="6.5" />
                    <path d="M20 20l-4.6-4.6" />
                  </svg>
                </span>
              )}
              <span className="hotspot-label">{h.label}</span>
            </button>
          );
        })}
      </div>

      <div className="pack-room-guide" aria-hidden>
        <div className="pack-room-guide-kicker">How this works</div>
        <ol className="pack-room-guide-steps">
          <li>
            <span className="pack-room-guide-badge">1</span>
            Tap an object to inspect it — see what&apos;s really there
          </li>
          <li>
            <span className="pack-room-guide-badge">2</span>
            Then decide inside: pack it, or leave it behind
          </li>
        </ol>
      </div>

      <div className="pack-room-hint" aria-hidden>
        🔍 Drag to look around · Tap an object to inspect — you decide after
      </div>

      {open && (
        <PackItemReveal
          item={open}
          packed={packed.has(open.id)}
          canToggle={open.role !== "info"}
          onToggle={() => doToggle(open)}
          onClose={() => setOpenId(null)}
        />
      )}

      <aside className="pack-bag" aria-label="Max's bag">
        <div className="pack-bag-head">
          <div className="rw-kicker">Remote work launch</div>
          <h2>{config.headline ?? "Pack for the trip"}</h2>
          <p className="rw-body">
            Tap an object to inspect it first — nothing is packed yet. Once you see what it really is, pack it or leave it behind from inside that card.
          </p>
        </div>
        <div className="pack-bag-list">
          <div className="pack-bag-label">In the bag ({packedItems.length})</div>
          {packedItems.length === 0 ? (
            <div className="pack-bag-empty">Nothing packed yet — explore the room.</div>
          ) : (
            <ul>
              {packedItems.map((i) => (
                <li key={i.id}>
                  <button type="button" onClick={() => toggle(i)} title="Remove">
                    {i.label}
                    <span aria-hidden>×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {hint && <div className="pack-bag-warn">{hint}</div>}
        </div>
        <button type="button" className="btn pack-done" onClick={submit}>
          Done — leave for the train
        </button>
      </aside>
    </div>
  );
}
